import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';
import { locale as deutsch } from './i18n/de';
import { FuseConfigService } from '@fuse/services/config.service';
import { WhiteLabelEntry } from '@myspot_org/ngx-core/models';
import { WhiteLabelService } from '@myspot_org/ngx-services';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { DOCUMENT } from '@angular/common';
import { interval, of, Subject, takeUntil } from 'rxjs';
import { DEFAULT_MYSPOT_UI } from './util/default-ui';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'myspot-crestron-ngx';
  whiteLabel: WhiteLabelEntry;
  fuseConfig: any;
  private _unsubscribeAll: Subject<void>;
  isfuseConfigLoaded = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public translateService: TranslateService,
    private _fuseConfigService: FuseConfigService,
    private whiteLabelService: WhiteLabelService,
    @Inject(OKTA_AUTH) public auth: OktaAuth,
  ) {
    this.translateService.addLangs(['en', 'it', 'de']);
    this.translateService.setTranslation(english.lang, english.data, true);
    this.translateService.setTranslation(italian.lang, italian.data, true);
    this.translateService.setTranslation(deutsch.lang, deutsch.data, true);
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    this.auth.authStateManager.subscribe((authState) => {
      if (!authState?.isAuthenticated) {
        return;
      }
      this.loadWhiteLabel();
    });
    this.auth.authStateManager.updateAuthState();
    this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.fuseConfig = config;
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      for (let i = 0; i < this.document.body.classList.length; i++) {
        const className = this.document.body.classList[i];
        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      }

      this.document.body.classList.add(this.fuseConfig.colorTheme);
      if (!this.isfuseConfigLoaded) {
        this.isfuseConfigLoaded = true;
      }
    });

    interval(60000).pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.refreshSession();
    });
  }

  applyWhiteLabel(entry?: WhiteLabelEntry): void {
    if (!entry) {
      return;
    }
    const ui = entry ?? (DEFAULT_MYSPOT_UI as WhiteLabelEntry);
    this._fuseConfigService.setConfig({ colorTheme: ui.colorTheme });
  }

  async loadWhiteLabel(): Promise<void> {
    const isAuthenticated = await this.auth.isAuthenticated();
    if (!isAuthenticated) {
      return;
    }

    const u = await this.auth.getUser();
    this.whiteLabel = this.whiteLabelService.resolveWhiteLabelByTenantCode(`${u.organization}`);
    this.applyWhiteLabel(this.whiteLabel);
  }

  async refreshSession(): Promise<void> {
    const session = await this.auth.session.get();
    let isAuthenticated = this.auth.isAuthenticated();
    if (!session || !isAuthenticated) {
      this.auth.start();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }
}
